import React, { useState } from 'react';
import { TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
	formatAsCustomDomain,
	getIsFreeCustomDomainName,
	getIsValidDomainName,
} from 'common/modules/customDomains/utils';
import BackdropDialog from 'common/components/BackdropDialog';
import { api } from 'common/frontend/api';
import { notUndefined } from 'common/utils/common';
import { useTranslation } from 'services/localization';

interface Props {
	currentDomainName: string | undefined;
	onConfirm: (domainName: string) => void;
	onCancel: () => void;
}

const DomainName = (props: Props) => {
	const { onConfirm, onCancel, currentDomainName } = props;
	const theme = useTheme();
	const { t } = useTranslation();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [domainName, setDomainName] = useState<string>(currentDomainName ?? '');
	const [domainSaveError, setDomainSaveError] = useState<string | null>(null);
	const [domainLoading, setDomainLoading] = useState<boolean>(false);

	const invalidDomainName = !!domainName && !getIsValidDomainName(domainName);

	const getDomainErrorText = () => {
		if (!!domainSaveError) return domainSaveError;
		if (!invalidDomainName) return null;
		const domainIncludesHttpOrHttps = domainName.startsWith('http');

		const invalidDomainText = t('account.domains.notValidDomainName', 'Not a valid domain name');
		const enterDomainWithoutHttpText = domainIncludesHttpOrHttps
			? t('account.domains.enterDomainWithoutHttp', 'Enter domain without http:// or https://')
			: undefined;
		const errorText = [invalidDomainText, enterDomainWithoutHttpText]
			.filter(notUndefined)
			.join('. ');
		return errorText;
	};

	const handleNextClick = async (domain: string) => {
		setDomainSaveError(null);
		setDomainLoading(true);
		const formattedDomainName = await formatAsCustomDomain(domain);
		if (!getIsValidDomainName(formattedDomainName)) {
			setDomainSaveError(t('account.domains.notValidDomainName', 'Not a valid domain name'));
			setDomainLoading(false);
			return;
		}
		const isFreeDomainName = await getIsFreeCustomDomainName(api(), formattedDomainName);
		if (!isFreeDomainName) {
			setDomainSaveError(t('account.domains.domainInUse', 'This domain is already in use'));
			setDomainLoading(false);
			return;
		}
		onConfirm(formattedDomainName);
		setDomainLoading(false);
	};

	const handleDomainNameChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		if (domainSaveError) {
			setDomainSaveError(null);
		}
		setDomainName(e.target.value.toLocaleLowerCase());
	};

	const domainError = getDomainErrorText();

	return (
		<BackdropDialog
			isOpen={true}
			onClose={onCancel}
			label={t('account.domains.connectExisting', 'Connect existing domain')}
			fullScreen={isMobile}
			body={
				<>
					<Typography variant="body2" color="secondary" sx={{ mb: 2 }}>
						{t(
							'account.domains.connectDomainInfo',
							'Enter the domain that you want to connect. It can be a domain (mystore.com) or a subdomain (booknow.mystore.com).',
						)}
					</Typography>
					<TextField
						autoFocus={true}
						fullWidth
						value={domainName}
						onChange={handleDomainNameChange}
						error={!!domainError}
						helperText={domainError}
					/>
				</>
			}
			cancel={{
				label: t('common:actions.cancel'),
				callback: onCancel,
				autoFocus: false,
			}}
			confirm={{
				disabled: !domainName || invalidDomainName,
				label: t('common:actions.next'),
				loading: domainLoading,
				disableAutoClose: true,
				autoFocus: false,
				callback: () => handleNextClick(domainName),
			}}
			backdropTimeout={0}
		/>
	);
};

export default DomainName;
